<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @search="handleSearch"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @user-menu="handleUserMenu"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="dashboard-view">
      <!-- Page Header -->
      <div class="dashboard-header">
        <div>
          <h1 class="dashboard-title">{{ $t('dashboard.title') }}</h1>
          <p class="dashboard-subtitle">{{ $t('dashboard.welcome') }}</p>
        </div>
        <IntercomButton variant="primary" @click="handleNewInvoice">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('dashboard.newInvoice') }}</span>
        </IntercomButton>
      </div>

      <!-- Stats Grid -->
      <div class="dashboard-stats-grid">
        <DashboardStatCard
          :label="$t('dashboard.stats.totalRevenue')"
          :value="stats.totalRevenue"
          currency
          :change="revenueChange.value"
          :change-type="revenueChange.type"
          :change-text="revenueChange.text"
          :animation-delay="0.05"
          clickable
        />
        <DashboardStatCard
          :label="$t('dashboard.stats.paidInvoices')"
          :value="stats.paidAmount"
          currency
          :change="paidChange.value"
          :change-type="paidChange.type"
          :change-text="paidChange.text"
          :animation-delay="0.1"
          clickable
        />
        <DashboardStatCard
          :label="$t('dashboard.stats.pendingAmount')"
          :value="stats.pendingAmount"
          currency
          :change="pendingChange.value"
          :change-type="pendingChange.type"
          :change-text="pendingChange.text"
          :animation-delay="0.15"
          clickable
        />
        <DashboardStatCard
          v-if="billingSettings.show_due_date !== false"
          :label="$t('dashboard.stats.overdue')"
          :value="stats.overdueAmount"
          currency
          :change="overdueChange.value"
          :change-type="overdueChange.type"
          :change-text="overdueChange.text"
          :animation-delay="0.2"
          clickable
        />
      </div>

      <!-- Recent Invoices -->
      <IntercomCard title="Recent Invoices" class="dashboard-recent-invoices">
        <template #header>
          <div class="dashboard-card-header">
            <h2 class="dashboard-card-title">{{ $t('dashboard.recentInvoices') }}</h2>
            <div class="dashboard-filters">
              <IntercomFilterButton
                v-for="filter in filters"
                :key="filter.value"
                :active="activeFilter === filter.value"
                @click="setFilter(filter.value)"
              >
                {{ filter.label }}
              </IntercomFilterButton>
            </div>
          </div>
        </template>

        <IntercomTable
          :columns="invoiceColumns"
          :data="filteredInvoices"
          :loading="isLoading"
          clickable
          @row-click="handleInvoiceClick"
        >
          <template #cell-invoice_number="{ value }">
            <span class="invoice-number">{{ value }}</span>
          </template>

          <template #cell-total_amount="{ value }">
            ₹{{ parseFloat(value).toLocaleString('en-IN') }}
          </template>

          <template #cell-status="{ value }">
            <IntercomBadge :variant="value.toLowerCase()">
              {{ value }}
            </IntercomBadge>
          </template>

          <template #cell-invoice_date="{ value }">
            {{ formatDate(value) }}
          </template>
        </IntercomTable>
      </IntercomCard>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomFilterButton,
  IntercomTable,
  IntercomBadge,
} from '@shared/components/intercom'
import DashboardStatCard from '../components/dashboard/DashboardStatCard.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { listInvoices, getDashboard, getBillingSettings, businessName, fetchBusinessName } = useBilling()

// Billing settings (controls due date / overdue visibility)
const billingSettings = ref({})

const navSections = billingNavSections

const isLoading = ref(false)
const invoices = ref([])
const dashboardStats = ref(null)
const activeFilter = ref('all')

const filters = computed(() => {
  const base = [
    { label: 'All', value: 'all' },
    { label: 'Paid', value: 'paid' },
    { label: 'Pending', value: 'pending' },
  ]
  if (billingSettings.value?.show_due_date !== false) {
    base.push({ label: 'Overdue', value: 'overdue' })
  }
  return base
})

const invoiceColumns = [
  { key: 'invoice_number', title: 'Invoice', width: '140px' },
  { key: 'customer_name', title: 'Customer', width: '200px' },
  { key: 'invoice_date', title: 'Date', width: '120px' },
  { key: 'total_amount', title: 'Amount', width: '120px', align: 'right' },
  { key: 'status', title: 'Status', width: '100px' },
]

// Calculate stats from API data
const stats = computed(() => {
  if (dashboardStats.value) {
    return {
      totalRevenue: dashboardStats.value.current_month?.total_revenue || 0,
      paidAmount: dashboardStats.value.current_month?.paid_amount || 0,
      pendingAmount: dashboardStats.value.current_month?.pending_amount || 0,
      overdueAmount: dashboardStats.value.current_month?.overdue_amount || 0,
    }
  }
  // Fallback to invoice-based calculation if API not loaded yet
  const paid = invoices.value.filter((inv) => inv.status === 'Paid')
  const pending = invoices.value.filter((inv) => inv.status === 'Pending')
  const overdue = invoices.value.filter((inv) => inv.status === 'Overdue')

  return {
    totalRevenue: invoices.value.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
    paidAmount: paid.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
    pendingAmount: pending.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
    overdueAmount: overdue.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
  }
})

// Get change info for a stat (handles null = new business, 0 = no change, +/- = real change)
const getChangeInfo = (changeValue, invertForOverdue = false) => {
  // Null means no previous data (fresh business)
  if (changeValue === null || changeValue === undefined) {
    return {
      value: null,
      type: 'neutral',
      text: t('dashboard.stats.newBusiness'),
    }
  }
  // Zero means no change
  if (changeValue === 0) {
    return {
      value: 0,
      type: 'neutral',
      text: t('dashboard.stats.noChange'),
    }
  }
  // For overdue: negative change is good (less overdue = positive)
  const isPositive = invertForOverdue ? changeValue < 0 : changeValue > 0
  const displayValue = `${changeValue > 0 ? '+' : ''}${changeValue.toFixed(1)}%`
  return {
    value: changeValue,
    type: isPositive ? 'positive' : 'negative',
    text: t('dashboard.stats.fromLastMonth', { value: displayValue }),
  }
}

// Computed change info for each stat
const revenueChange = computed(() => getChangeInfo(dashboardStats.value?.revenue_change))
const paidChange = computed(() => getChangeInfo(dashboardStats.value?.paid_change))
const pendingChange = computed(() => getChangeInfo(dashboardStats.value?.pending_change))
const overdueChange = computed(() => getChangeInfo(dashboardStats.value?.overdue_change, true))

// Filter invoices based on active filter
const filteredInvoices = computed(() => {
  if (activeFilter.value === 'all') {
    return invoices.value.slice(0, 8) // Show only 8 recent
  }
  return invoices.value
    .filter((inv) => inv.status.toLowerCase() === activeFilter.value)
    .slice(0, 8)
})

const setFilter = (filter) => {
  activeFilter.value = filter
}

const handleSearch = (query) => {
  console.log('Search:', query)
  // Implement search functionality
}

const handleNotifications = () => {
  console.log('Notifications clicked')
  // Implement notifications
}

const handleSettings = () => {
  console.log('Settings clicked')
  router.push('/settings')
}

const handleUserMenu = () => {
  console.log('User menu clicked')
  // Implement user menu
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    // Even if API fails, redirect to login
    router.push('/login')
  }
}

const handleNewInvoice = () => {
  router.push('/invoices')
}

const handleInvoiceClick = (invoice) => {
  router.push(`/invoices/${invoice.id}`)
}

const fetchDashboardData = async () => {
  isLoading.value = true
  try {
    // Fetch both dashboard stats and invoices in parallel
    const [dashboardResponse, invoicesResponse] = await Promise.all([
      getDashboard(),
      listInvoices(),
    ])
    dashboardStats.value = dashboardResponse
    invoices.value = invoicesResponse.data || []
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  fetchBusinessName()
  // Fetch billing settings to control overdue visibility
  try {
    const s = await getBillingSettings()
    billingSettings.value = s?.data || s || {}
  } catch { /* defaults apply */ }
  fetchDashboardData()
})
</script>

<style scoped>
/* Import Intercom styles */
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

/* ========================================
   DASHBOARD VIEW
   ======================================== */

/* ========================================
   PAGE HEADER
   ======================================== */
.dashboard-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dashboard-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.dashboard-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* ========================================
   STATS GRID
   ======================================== */
.dashboard-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--space-5);
  margin-bottom: var(--space-8);
}

/* ========================================
   RECENT INVOICES
   ======================================== */
.dashboard-recent-invoices {
  animation: fadeInUp 0.4s ease-out 0.25s backwards;
}

.dashboard-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dashboard-card-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.dashboard-filters {
  display: flex;
  gap: var(--space-2);
}

.invoice-number {
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
}

/* ========================================
   RESPONSIVE
   ======================================== */
@media (max-width: 768px) {

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .dashboard-stats-grid {
    grid-template-columns: 1fr;
  }

  .dashboard-title {
    font-size: 26px;
  }
}
</style>
